import type { Ref } from 'vue';
import { useUiStore } from '~/store/ui';

type AdditionalHeaders = {
  [key: string]: string;
}

type Options = {
  method: 'GET' | 'POST',
  body?: object,
  headers: AdditionalHeaders
}

export default function useApiFetch() {
  const lastError = ref(null) as any;
  const lastErrorCode = ref<number | null>(null);
  const lastCentraErrors = ref<any>(null);
  const lastResponse = ref(null) as any;
  const lastResponseCode = ref<number | null>(null);
  const runtimeConfig = useRuntimeConfig();
  const route = useRoute();
  const routeSplits = route.path.split('/');
  const countryCode = routeSplits && routeSplits.length >= 1 ? routeSplits[1] : 'se';
  const centraToken = useCookie('centraToken', { maxAge: 3600 * 24 * 182, path: '/' });
  const { sellerCookieOptions } = useCookieConfig();
  const isSeller = useCookie<boolean>('isSeller', sellerCookieOptions) as Ref<boolean>;
  const splitRoute = route.path.split('/');

  const apiGet = async<T>(url: string)=> {
    // Requests to wordpress
    if (!url.startsWith('http')) {
      if (countryCode === '.well-known') {
        url = `${runtimeConfig.public.apiUrl}/se${url}`;
      } else {
        url = `${runtimeConfig.public.apiUrl}/${countryCode}${url}`;
      }
      /**
       * The reason we check route both here and in init.server.ts
       * is because isSeller cookie is not update that early in the vue life cycle.
       */
      if (isSeller.value || splitRoute[2] === 'group' || splitRoute[2] === 'seller' || route.query?.seller === 'true') {
        url += url.includes('?') ? '&type=sales' : '?type=sales';
      }
    }
    return req<T>(url, 'GET');
  };

  const unifaunGet = async<T>(url: string) => {
    return req<T>(url, 'GET');
  };

  const apiPost = async<T>(
    url: string,
    body: object | null = null
  )=> {
    if (!url.startsWith('http')) {
      url = `${runtimeConfig.public.apiUrl}/${countryCode}${url}`;
    }
    return req<T>(url, 'POST', body);
  };

  const unifaunPost = async<T>(
    url: string,
    body: object | null = null
  )=> {
    return req<T>(url, 'POST', body);
  };

  const centraPost = async<T>(
    url: string,
    body: object | null = null,
    additionalHeaders: AdditionalHeaders = {}
  )=> {
    if (centraToken.value) {
      additionalHeaders['API-token'] = centraToken.value;
    }
    return req<T>(`/api${url}`, 'POST', body, additionalHeaders);
  };

  const centraDelete = async<T>(
    url: string,
    body: object | null = null,
    additionalHeaders: AdditionalHeaders = {}
  )=> {
    if (centraToken.value) {
      additionalHeaders['API-token'] = centraToken.value;
    }
    return req<T>(`/api${url}`, 'DELETE', body, additionalHeaders);
  };

  const centraPut = async<T>(
    url: string,
    body: object | null = null,
    additionalHeaders : AdditionalHeaders = {}
  )=> {
    if (centraToken.value) {
      additionalHeaders['API-token'] = centraToken.value;
    }
    return req<T>(`/api${url}`, 'PUT', body, additionalHeaders);
  };

  const centraGet = async<T>(
    url: string,
    additionalHeaders : AdditionalHeaders = {}
  )=> {
    if (centraToken.value) {
      additionalHeaders['API-token'] = centraToken.value;
    }
    return req<T>(`/api${url}`, 'GET', null, additionalHeaders);
  };

  const req = async<T>(
    url: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    body: object | null = null,
    additionalHeaders : null | AdditionalHeaders = null
  ) => {
    lastResponse.value = null;
    lastResponseCode.value = null;
    lastError.value = null;
    lastErrorCode.value = null;
    lastCentraErrors.value = null;

    const opts = {
      method,
    } as Options;

    if (body) {
      opts.body = body;
    }

    if (additionalHeaders) {
      opts.headers = {
        ...additionalHeaders,
      };
    }

    /**
     * @todo - accessing a non existing url it tries to redirect two times on the server, so error handling is not working here
     */
    try {
      const res = await $fetch<any>(
        url, {
          ...opts,
          async onRequestError({ request, options, error }) {
          },
          async onResponseError({ request, response }) {
            lastErrorCode.value = response.status;

            if (response._data?.statusMessage) {
              lastError.value = response._data?.statusMessage;
            }
            else if (response._data?.message) {
              lastError.value = response._data?.message;
            }

            // Error format from centra:
            if (response._data?.data?.errors) {
              lastCentraErrors.value = response._data.data.errors;
            }
          },
        });

      if (res && res.token && res.token !== '') {
        centraToken.value = res.token;
      }

      return res as T;
    } catch (e: any) {
      // Network-level failures (DNS, connection refused, timeout) never hit onResponseError,
      // so log them here or they disappear silently and just look like an empty response.
      console.error('[API FETCH] Request failed:', url, e?.message || e);
      return null;
    }
  };

  const handleApiError = (customText: string | null = null, customTime = 0) => {
    const uiStore = useUiStore();

    if (!customText) {
      customText = lastError.value ;
    }

    uiStore.setError(customText || 'No error desciption', customTime);
  };

  return {
    apiGet,
    centraPost,
    centraPut,
    centraGet,
    centraDelete,
    apiPost,
    unifaunGet,
    unifaunPost,
    lastErrorCode,
    lastError,
    lastCentraErrors,
    handleApiError,
  };
}
